import { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/RecordingOverlay.css';
import { formatTimePadded } from '../utils/formatTime';

interface RecordingOverlayProps {
  isRecording: boolean;
  recordingUrl: string | null;
  recordingBlob: Blob | null;
  error: string | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onClearRecording: () => void;
  onSubmit: (blob: Blob) => void;
  onClose: () => void;
  isUploading: boolean;
  uploadProgress: number;
  /** Live MediaStream for camera preview */
  stream?: MediaStream | null;
  /** Whether recording includes video */
  hasVideo?: boolean;
  /** Custom label for the submit button (default: "Submit for Review") */
  submitLabel?: string;
}

export function RecordingOverlay({
  isRecording,
  recordingUrl,
  recordingBlob,
  error,
  onStartRecording,
  onStopRecording,
  onClearRecording,
  onSubmit,
  onClose,
  isUploading,
  uploadProgress,
  stream,
  hasVideo,
  submitLabel = 'Submit for Review',
}: RecordingOverlayProps) {
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Timer for recording duration
  useEffect(() => {
    if (isRecording) {
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  // Wire live stream to preview video element via ref callback
  // (useEffect + ref doesn't work because the element mounts after stream is set)
  const previewRefCallback = useCallback((el: HTMLVideoElement | null) => {
    if (el && stream) {
      el.srcObject = stream;
    }
  }, [stream]);

  const handleReRecord = useCallback(() => {
    onClearRecording();
  }, [onClearRecording]);

  const handleSubmit = useCallback(() => {
    if (recordingBlob) {
      onSubmit(recordingBlob);
    }
  }, [recordingBlob, onSubmit]);

  // Determine state: idle, recording, or review
  const hasRecording = !isRecording && recordingUrl !== null;

  return (
    <div className="recording-overlay">
      {/* Camera preview — shown in idle and recording states when video is available */}
      {hasVideo && stream && !hasRecording && (
        <div className="recording-preview">
          <video
            ref={previewRefCallback}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              maxWidth: 320,
              borderRadius: 'var(--radius-md)',
              transform: 'scaleX(-1)',
              background: '#000',
            }}
          />
        </div>
      )}

      {/* Idle state - ready to record */}
      {!isRecording && !hasRecording && (
        <>
          <button
            onClick={onStartRecording}
            className="capsule-btn capsule-btn--record"
            disabled={isUploading}
          >
            Record
          </button>
          <button
            onClick={onClose}
            className="capsule-btn capsule-btn--ghost"
          >
            Cancel
          </button>
        </>
      )}

      {/* Recording state */}
      {isRecording && (
        <>
          <div className="recording-indicator">
            <span className="recording-dot" />
            <span className="recording-timer">{formatTimePadded(elapsed)}</span>
          </div>
          <button
            onClick={onStopRecording}
            className="capsule-btn capsule-btn--warning"
          >
            Stop
          </button>
        </>
      )}

      {/* Review state */}
      {hasRecording && (
        <div className="recording-review">
          <video
            src={recordingUrl!}
            controls
            playsInline
            style={{
              width: '100%',
              maxWidth: 400,
              maxHeight: 300,
              borderRadius: 'var(--radius-md)',
              background: '#000',
            }}
          />
          <button
            onClick={handleReRecord}
            className="capsule-btn capsule-btn--ghost"
            disabled={isUploading}
          >
            Re-record
          </button>
          <button
            onClick={handleSubmit}
            className="capsule-btn capsule-btn--submit"
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : submitLabel}
          </button>
          {isUploading && uploadProgress > 0 && (
            <span className="recording-upload-progress">{uploadProgress}%</span>
          )}
          <button
            onClick={onClose}
            className="capsule-btn capsule-btn--ghost"
            disabled={isUploading}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error display */}
      {error && <span className="recording-error">{error}</span>}
    </div>
  );
}
